import { useEffect, useState } from "react";
import { MESES, aniosOfrecidos, esMayorDe14 } from "@/lib/edad.mjs";

/**
 * En qué año y mes naciste — y, si sale que menos de catorce, el correo de quien decide.
 *
 * POR QUE NO ES UNA CASILLA de «tengo 14 años o más». Esa casilla canta cuál es la respuesta
 * que abre la puerta: le enseña a mentir al que tiene doce y no se lo pone difícil a nadie.
 * Un año de nacimiento no telegrafía nada. El mes hace falta porque con el año solo no se
 * sabe: quien nació en 2012, en 2026 tiene trece o catorce según el día.
 *
 * Y NO SE GUARDA. Los dos números viajan al servidor, que calcula el sí o el no y se queda
 * solo con eso. Está dicho aquí, en pantalla, porque una plataforma que le pide la fecha de
 * nacimiento a un menor y no explica qué hace con ella ya ha perdido la conversación.
 *
 * La misma regla vive en el servidor (`backend/app/domain/edad.py`), que es la que decide de
 * verdad; ésta solo sirve para saber si hay que pedir el correo del tutor antes de enviar.
 * `tools/check_edad.py` ejecuta las dos y se cae si se separan.
 */
export default function PreguntaDeEdad({ valor, onCambia, autoFocus = false }) {
  const { anio = "", mes = "", emailTutor = "" } = valor || {};
  const [anios] = useState(() => aniosOfrecidos());
  const mayor = anio && mes ? esMayorDe14(Number(anio), Number(mes)) : null;

  useEffect(() => {
    onCambia({ ...(valor || {}), mayor });
    // `mayor` se deriva de anio y mes: avisar al padre en cuanto cambia evita que el
    // formulario se envíe con un estado de ayer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mayor]);

  const campo =
    "w-full px-4 py-3 bg-white border border-alpha-black/15 rounded focus:border-alpha-red outline-none";
  const etiqueta =
    "block font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/50 mb-2";

  return (
    <div className="space-y-4" data-testid="pregunta-edad">
      <div>
        <label className={etiqueta}>¿En qué año y mes naciste?</label>
        <div className="flex gap-2">
          <select
            className={campo}
            value={mes}
            autoFocus={autoFocus}
            onChange={(e) => onCambia({ ...(valor || {}), mes: e.target.value })}
            data-testid="edad-mes"
          >
            <option value="">Mes</option>
            {MESES.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            className={campo}
            value={anio}
            onChange={(e) => onCambia({ ...(valor || {}), anio: e.target.value })}
            data-testid="edad-anio"
          >
            <option value="">Año</option>
            {anios.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <p className="text-[12px] text-alpha-black/50 mt-2 leading-snug">
          No lo guardamos. Solo sirve para saber si hace falta el permiso de tu padre, tu
          madre o tu tutor, que en España es obligatorio por debajo de los 14.
        </p>
      </div>

      {mayor === false && (
        <div className="border-l-2 border-alpha-red pl-4" data-testid="edad-tutor">
          {/* HACE FALTA PARA ABRIR LA CUENTA, y hasta el 1 de octubre de 2026 no lo decia ni
              lo pedia: sin el, la cuenta nacia parada esperando un permiso que no se le podia
              pedir a nadie. */}
          <label className={etiqueta}>Correo de tu padre, madre o tutor · hace falta</label>
          <input
            type="email"
            required
            className={campo}
            value={emailTutor}
            onChange={(e) => onCambia({ ...(valor || {}), emailTutor: e.target.value })}
            placeholder="para pedirle permiso"
          />
          <p className="text-[12px] text-alpha-black/50 mt-2 leading-snug">
            Le escribimos explicándole qué es esto. Hasta que dé el permiso, la cuenta se
            queda parada. Si tu profesor usa la Academia en clase, pídele el código de tu
            grupo y entras sin esperar a nadie.
          </p>
        </div>
      )}
    </div>
  );
}
